import { getDateFromBackendISO } from 'utils/dates';
import { getLiteralWithParameters } from 'utils/getLiteral';

import { parseCampaignSenderIdField } from '../utils/marketingSenderOptions';
import {
    MARKETING_CAMPAIGN_STATUS,
    normalizeMarketingCampaignStatusId,
} from '../utils/marketingCampaignStatus';

/** Prefer `launchAt`, then `scheduleAt` (no `updatedAt` / `createdAt`). */
function pickLaunchOrScheduleDateBlock({ launchAt, scheduleAt }) {
    if (launchAt?.time) return launchAt;
    if (scheduleAt?.time) return scheduleAt;
    return null;
}

/**
 * Meta line parts: `#id` (bold) and date sentence (muted), rendered separately in the UI.
 * Date always comes from `launchAt` or `scheduleAt`.
 * @param {Record<string, unknown>} campaign
 * @returns {{ idPart: string, detail: string }}
 */
export function buildCampaignDetailsMetaParts(campaign) {
    const id = campaign?.id;
    const idPart = id !== undefined && id !== null && id !== '' ? `#${id}` : '';

    const statusId = normalizeMarketingCampaignStatusId(campaign?.status);
    const isSent = statusId === MARKETING_CAMPAIGN_STATUS.SENT;

    const { launchAt, scheduleAt } = campaign ?? {};
    const dateBlock = pickLaunchOrScheduleDateBlock({ launchAt, scheduleAt });
    const time = dateBlock?.time;

    if (!time) {
        return { idPart, detail: '' };
    }

    if (isSent) {
        const datePart = getDateFromBackendISO(time, 'LL');
        const timePart = getDateFromBackendISO(time, 'LT');
        const detail = getLiteralWithParameters('label_sent_on_at', [datePart, timePart]);
        return { idPart, detail };
    }

    const datePart = getDateFromBackendISO(time, 'LL');
    const timePart = getDateFromBackendISO(time, 'HH:mm');
    const detail = getLiteralWithParameters('label_last_modified', [datePart, timePart]);
    return { idPart, detail };
}

/**
 * "Name <email>" when both exist; otherwise email or parsed sender label.
 * @param {Record<string, unknown>} campaign
 * @returns {string}
 */
export function formatFromLine(campaign) {
    const senderParsed = parseCampaignSenderIdField(campaign?.senderId);
    const email =
        (typeof campaign?.senderEmail === 'string' && campaign.senderEmail.trim()) ||
        senderParsed.label ||
        '';
    const name = typeof campaign?.senderName === 'string' ? campaign.senderName.trim() : '';

    if (name && email) {
        return `${name} <${email}>`;
    }
    return email || name || '';
}
